import { useQuery } from "@apollo/client";
import { Refresh, RemoveCircle, Search } from "@mui/icons-material";
import {
  Card,
  CardHeader,
  Typography,
  CardContent,
  Chip,
  Button,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useConfirm } from "material-ui-confirm";
import { ReactElement, useCallback, useState } from "react";

import {
  InboxType,
  ListCampaignInboxUsersQuery,
  ListCampaignInboxUsersQueryVariables,
  RemoveUserFromInboxMutation,
  RemoveUserFromInboxMutationVariables,
  RotateInboxJoinTokenMutation,
  RotateInboxJoinTokenMutationVariables,
} from "~frontend/__generated__/graphql";
import ClickToCopy from "~frontend/components/ClickToCopy";
import ErrorCard from "~frontend/components/ErrorCard";
import { FlexBox } from "~frontend/components/FlexBox";
import { InternalLink } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import Table from "~frontend/components/Table";
import { TwoColumnDisplay } from "~frontend/components/TwoColumnDisplay";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import { GET_CAMPAIGN_FOR_VIEW } from "~frontend/graphql/campaigns";
import {
  LIST_INBOX_USERS,
  REMOVE_USER_FROM_INBOX,
  ROTATE_INBOX_JOIN_TOKEN,
} from "~frontend/graphql/inboxes";
import { palette } from "~frontend/theme";
import useMutation from "~frontend/util/useMutation";

import { CampaignViewAccordion } from "./CampaignViewAccordion";
import { buildTexterLink } from "./texterLink";
import { CampaignForView } from "./types";

export function Texters({
  campaign,
}: {
  campaign: CampaignForView;
}): ReactElement {
  const confirm = useConfirm();

  const [search, setSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);

  const { data, loading, error } = useQuery<
    ListCampaignInboxUsersQuery,
    ListCampaignInboxUsersQueryVariables
  >(LIST_INBOX_USERS, {
    variables: {
      input: {
        campaignId: campaign.id,
        nameOrEmailContains: search,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      },
    },
  });

  const [removeUserFromInbox] = useMutation<
    RemoveUserFromInboxMutation,
    RemoveUserFromInboxMutationVariables
  >(REMOVE_USER_FROM_INBOX, {
    refetchQueries: [LIST_INBOX_USERS],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Error removing texter.",
  });

  const handleRemoveUser = useCallback(
    async ({ userInboxId }: { userInboxId: string }) => {
      await removeUserFromInbox({
        variables: { input: { userInboxId } },
      });
    },
    [removeUserFromInbox]
  );

  const texterLink = buildTexterLink(campaign);

  const [rotatingJoinToken, setRotatingJoinToken] = useState(false);

  const [rotateJoinToken] = useMutation<
    RotateInboxJoinTokenMutation,
    RotateInboxJoinTokenMutationVariables
  >(ROTATE_INBOX_JOIN_TOKEN, {
    variables: { input: { inboxId: campaign.inbox!.id } },
    refetchQueries: [GET_CAMPAIGN_FOR_VIEW],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Error rotating join token.",
  });

  const handleRotateJoinToken = useCallback(async () => {
    const { confirmed } = await confirm({
      title: "Warning",
      description:
        "Rotating your texter link will create a new url and make the previous link unusable.",
      confirmationText: "Rotate Texter Link",
    });
    if (!confirmed) {
      return;
    }

    setRotatingJoinToken(true);
    await rotateJoinToken();
    setRotatingJoinToken(false);
  }, [confirm, setRotatingJoinToken, rotateJoinToken]);

  return (
    <CampaignViewAccordion
      header="Texters"
      sx={{ backgroundColor: palette.white, mt: 0 }}
    >
      <Formik
        initialValues={{ search }}
        onSubmit={values => setSearch(values.search)}
      >
        {({ submitForm }) => (
          <Form>
            <FormikTextField
              fullWidth
              name="search"
              onBlur={submitForm}
              placeholder="Search for a texter"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={submitForm}>
                      <Search />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  border: `1px solid ${palette.midGray} !important`,
                  fontSize: "12px",
                },
              }}
            />
          </Form>
        )}
      </Formik>
      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorCard errors={[error]} />
      ) : (
        <Table
          columns={[
            {
              key: "name",
              title: "Name",
              renderValue: ({ userInbox }) => userInbox.user.fullName,
            },
            {
              key: "email",
              title: "Email",
              renderValue: ({ userInbox }) => userInbox.user.email,
              align: "left",
            },
            {
              key: "replies",
              title: "Replies Sent",
              renderValue: ({ numRepliesSent }) => numRepliesSent,
              align: "left",
            },
          ]}
          rowActions={({ userInbox }) => [
            {
              id: "remove",
              icon: <RemoveCircle />,
              tooltipLabel: "Remove texter",
              onClick: () => handleRemoveUser({ userInboxId: userInbox.id }),
              confirm: {
                title: "Remove Texter",
                description: `This will remove ${userInbox.user.fullName} from this campaign.`,
              },
            },
          ]}
          rows={data!.listCampaignInboxUsers.userInboxes}
          totalCount={data!.listCampaignInboxUsers.totalCount}
          rowsPerPage={rowsPerPage}
          onChangeRowsPerPage={setRowsPerPage}
          page={page}
          onChangePage={setPage}
          emptyState="No texters in this campaign"
        />
      )}
      <Card>
        <CardHeader
          title={
            <Typography variant="body2Bold">Texter Information</Typography>
          }
        />
        <CardContent>
          <TwoColumnDisplay
            labelWidth="180px"
            showTopBorder={false}
            showBottomBorder={false}
            allRowSx={{ alignItems: "flex-start" }}
            rows={[
              {
                key: "texterLink",
                label: <Typography variant="body2Bold">Texter Link</Typography>,
                visible: campaign.inbox.type === InboxType.Campaign,
                content: (
                  <>
                    <FlexBox
                      justifyContent="space-between"
                      sx={{ marginBottom: "8px" }}
                    >
                      <ClickToCopy
                        text={texterLink.fullLink}
                        IconButtonProps={{
                          sx: {
                            color: palette.blue,
                            padding: 0,
                            width: "16px",
                          },
                        }}
                        iconSx={{ width: "16px" }}
                        TextProps={{
                          sx: { marginTop: "4px", marginLeft: "8px" },
                        }}
                      >
                        <InternalLink
                          to={texterLink.path}
                          target="_blank"
                          sx={{
                            color: palette.blue,
                            textDecoration: "none",
                          }}
                        >
                          {texterLink.fullLink.slice(0, 80)}...
                        </InternalLink>
                      </ClickToCopy>
                      <Button
                        variant="contained"
                        color="tertiary"
                        onClick={handleRotateJoinToken}
                        disabled={rotatingJoinToken}
                        startIcon={<Refresh />}
                        sx={{ padding: "0 8px", height: "27px" }}
                      >
                        <Typography variant="h4Caps">
                          Rotate Texter Link
                        </Typography>
                      </Button>
                    </FlexBox>

                    <Typography variant="body2" sx={{ marginBottom: "8px" }}>
                      Use this link to invite texters to your campaign.
                    </Typography>
                    <Typography variant="body2">
                      <Typography variant="body2Bold">Note:</Typography> a
                      texter must already have an account with your organization
                      to use this link. If they do not, you can send them an
                      invite from the people page. If the texter has an account
                      with a different subaccount in your organization, they
                      will be able to use this link and will be automatically
                      added as a texter to your subaccount.
                    </Typography>
                  </>
                ),
              },
              {
                key: "contactFields",
                label: (
                  <Typography variant="body2Bold">
                    Visible Contact Fields
                  </Typography>
                ),
                content: (
                  <>
                    <FlexBox gap="4px" sx={{ paddingBottom: "10px" }}>
                      {campaign.contactFields
                        .filter(field => field.isTexterViewable)
                        .map(field => (
                          <Chip
                            key={field.id}
                            label={`{${field.name}}`}
                            sx={{
                              backgroundColor: palette.midBlue,
                              fontSize: "10px",
                              height: "initial",
                              "& .MuiChip-label": { padding: "2px 6px" },
                            }}
                          />
                        ))}
                    </FlexBox>
                    <Typography variant="body2">
                      These are custom fields that are included in your
                      campaign, either through initial messages or response
                      scripts.
                      <br />
                      <Typography variant="body2Bold">
                        Contact information for these fields will be viewable by
                        your texters.
                      </Typography>
                    </Typography>
                  </>
                ),
              },
            ]}
          />
        </CardContent>
      </Card>
    </CampaignViewAccordion>
  );
}
